import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button } from '../../../../../ui';
import { TextArea } from '../../../../../ui/input/text_area/text_area';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { ToggleInput } from '../../../../../ui/input/toggle_input/toggle_input';
import {
  PrimaryTypography,
  SecondaryTypography,
} from '../../../../../ui/typography/typography';
import {
  getAssessment,
  getAssessmentSettings,
  updateAssessmentSettings,
  updateAssessmentStatus,
} from '../../../../../utils/api_service/assessment_api';
import { AssessmentContext } from '../../../../../utils/contexts/contexts';
import {
  Assessment,
  AssessmentSettings,
} from '../../../../../utils/types/assessment_types';
import styles from '../create_questions/create_questions.module.css';

export const SettingsAndPermissions = () => {
  const history = useHistory();
  const { assessmentState, setAssessmentState } = useContext(AssessmentContext);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [settings, setSettings] = useState<AssessmentSettings>({
    assessmentId: assessmentState.assessmentId,
    showScore: false,
    customMessage: '',
    shuffleQuestions: false,
    enableCopy: false,
    attemptCount: '1',
    blurVideo: false,
    unsuccessfulEmail: false,
    thresholdPercent: '0',
  });

  useEffect(() => {
    getAssessment(assessmentState.assessmentId).then((response: Assessment) => {
      if (response.id === assessmentState.assessmentId) {
        setIsActive(response.isActive);
        getAssessmentSettings(assessmentState.assessmentId).then(
          (response: AssessmentSettings) => {
            if (response.assessmentId === assessmentState.assessmentId) {
              setSettings({
                ...response,
                attemptCount: response.attemptCount.toString(),
                thresholdPercent:
                  response.thresholdPercent &&
                  response.thresholdPercent.toString(),
              });
            }
          },
        );
      }
    });
  }, [assessmentState.assessmentId]);

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSettings({
      ...settings,
      [event.target.name]: event.target.value,
    });
  };

  const navigateBackToQuestions = () => {
    saveAssessment();
    setAssessmentState({
      ...assessmentState,
      assessmentPageNo: 2,
    });
  };

  const saveAssessment = () => {
    updateAssessmentStatus(assessmentState.assessmentId, isActive).then(
      (response) => {
        if (response.ok) {
          updateAssessmentSettings(assessmentState.assessmentId, settings).then(
            (response) => {
              if (response.ok) {
                history.push('./assessment-list');
              }
            },
          );
        }
      },
    );
  };

  return (
    <div className='w-full h-full flex flex-col gap-5'>
      <nav
          className='flex w-full justify-between items-center border-b py-3 text-sm'
          aria-label='Breadcrumb'
      >
        <ol className='inline-flex items-center space-x-1 md:space-x-2'>
          <li>
            <Link to={'#'} className='text-primary-textGray hover:underline'>
              Create Assesment
            </Link>
          </li>
          <li>
            <span className='text-gray-400'>/</span>
          </li>
          <li>
            <Link to={'#'} className=' hover:underline'>
              Permissions and Settings
            </Link>
          </li>
        </ol>
      </nav>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 divide-x divide-gray-200'>
        <div className='space-y-4 divide-y divide-gray-200'>
          { /* Custom Message */ }
          <div className='pt-4 grid grid-cols-1 gap-4'>
            <TextArea
                label='Custom End-of-Test Message'
                name='customMessage'
                value={settings.customMessage}
                onChange={handleInputChange}
            />
          </div>
          { /* Attempt Count */ }
          <div className='pt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
            <div className='md:col-span-2'>
              <h3 className='font-semibold text-gray-800'>
                Number of Attempts
              </h3>
              <p className='text-gray-500 text-sm mt-1'>
                Maximum number of times a candidate can retake the assessment.
              </p>
            </div>
            <TextInput
                label=''
                name='attemptCount'
                value={settings.attemptCount}
                type='number'
                onChange={handleInputChange}
                min='1'
                max='10'
                className='w-24 justify-self-end'
            />
          </div>
          <div className='pt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
            <div className='md:col-span-2'>
              <h3 className='font-semibold text-gray-800'>
                Threshold Percentage
              </h3>
              <p className='text-gray-500 text-sm mt-1'>
                Minimum score (%) required to pass.
              </p>
            </div>
            <TextInput
                label=''
                name='thresholdPercent'
                value={settings.thresholdPercent || '0'}
                type='number'
                onChange={handleInputChange}
                min='1'
                max='100'
                className='w-24 justify-self-end'
            />
          </div>
          { /* Status */ }
          <div className='pt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
            <div className='md:col-span-2'>
              <h3 className='font-semibold text-gray-800'>Status of Test</h3>
              <p className='text-gray-500 text-sm mt-1'>
                Switch to make this assessment active or inactive.
              </p>
            </div>
            <ToggleInput
                toggled={isActive}
                handleToggle={() => setIsActive(!isActive)}
                name='isActive'
                informationText='Switch to make active/inactive'
            />
          </div>

          { /* Show Score */ }
          <div className='pt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
            <div className='md:col-span-2'>
              <h3 className='font-semibold text-gray-800'>Show Score at End</h3>
              <p className='text-gray-500 text-sm mt-1'>
                Display the candidate’s total score when they finish.
              </p>
            </div>
            <ToggleInput
                toggled={settings.showScore}
                handleToggle={() =>
                  setSettings({ ...settings, showScore: !settings.showScore })
                }
                name='showScore'
                informationText='Show total score'
            />
          </div>
        </div>{ ' ' }
        <div className='p-4 space-y-4 divide-y divide-gray-200'>
          { /* Shuffle Questions */ }
          <div className='pt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
            <div className='md:col-span-2'>
              <h3 className='font-semibold text-gray-800'>Shuffle Questions</h3>
              <p className='text-gray-500 text-sm mt-1'>
                Randomize the order of questions for each candidate.
              </p>
            </div>
            <ToggleInput
                toggled={settings.shuffleQuestions}
                handleToggle={() =>
                  setSettings({
                    ...settings,
                    shuffleQuestions: !settings.shuffleQuestions,
                  })
                }
                name='shuffleQuestions'
                informationText='Shuffle question order'
            />
          </div>

          { /* Disable Copy/Paste */ }
          <div className='pt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
            <div className='md:col-span-2'>
              <h3 className='font-semibold text-gray-800'>
                Restrict Cut/Copy/Paste
              </h3>
              <p className='text-gray-500 text-sm mt-1'>
                Prevent candidates from copying or pasting content during the
                test.
              </p>
            </div>
            <ToggleInput
                toggled={!settings.enableCopy}
                handleToggle={() =>
                  setSettings({ ...settings, enableCopy: !settings.enableCopy })
                }
                name='enableCopy'
                informationText='Restrict cut/copy/paste'
            />
          </div>

          { /* Blur Video */ }
          <div className='pt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
            <div className='md:col-span-2'>
              <h3 className='font-semibold text-gray-800'>
                Blur Candidate Video
              </h3>
              <p className='text-gray-500 text-sm mt-1'>
                Maintain candidate anonymity by blurring their video feed.
              </p>
            </div>
            <ToggleInput
                toggled={settings.blurVideo}
                handleToggle={() =>
                  setSettings({ ...settings, blurVideo: !settings.blurVideo })
                }
                name='blurVideo'
                informationText='Blur face of the candidate'
            />
          </div>

          { /* Candidate-specific Settings */ }
          { assessmentState.assessmentType === 'candidate' && (
            <>
              <div className='pt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
                <div className='md:col-span-2'>
                  <h3 className='font-semibold text-gray-800'>
                    Automatic Unsuccessful Email
                  </h3>
                  <p className='text-gray-500 text-sm mt-1'>
                    Send an email when a candidate does not meet the threshold.
                  </p>
                </div>
                <ToggleInput
                    toggled={settings.unsuccessfulEmail || false}
                    handleToggle={() =>
                      setSettings({
                        ...settings,
                        unsuccessfulEmail: !settings.unsuccessfulEmail,
                      })
                    }
                    name='unsuccessfulEmail'
                    informationText='Automatic Unsuccessful Email'
                />
              </div>
            </>
          ) }
        </div>
      </div>

      <div className='flex justify-end gap-3 pt-4 border-t'>
        <Button
            onClick={navigateBackToQuestions}
            variant='secondary'
            label='Back'
        />
        <Button onClick={saveAssessment} variant='primary' label='Save' />
      </div>
    </div>
  );
};
